import React, { useState } from 'react'
import {
  MapPin, TrendingUp, ChevronDown, ChevronUp, Calculator,
  Lightbulb, ArrowRight, ArrowLeftRight, CheckCircle, XCircle,
  Briefcase, Star, Trophy, ChevronRight, Zap, Globe
} from 'lucide-react'

// ── 직업 스토리 시나리오 데이터 ───────────────────────────────────────────────
const SCENARIOS = [
  {
    id: 'designer',
    emoji: '🎨',
    title: '패션 디자이너',
    category: '패션·섬유',
    color: 'from-emerald-400 to-teal-500',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    badge: 'bg-emerald-100 text-emerald-700',
    story: `패션 디자이너 소연씨는 정오각형 로고를 원단에 패턴으로 인쇄하려 합니다.
오각형의 내각의 합을 알아야 로고를 정확하게 배치할 수 있습니다.
"n각형 내각의 합 공식 (n-2)×180°를 적용하면 되겠어!"
내각의 합 = 540°이므로 한 내각 = 540÷5 = 108°임을 확인했습니다.`,
    situation: '정오각형(n=5) 내각의 합 = (5-2)×180° = 3×180° = 540°. 한 내각 = 540°÷5 = 108°.',
    question: '정오각형 한 내각의 크기는 몇 도인가요?',
    xLabel: '꼭짓점 수 n',
    yLabel: '내각의 합 (°)',
    slope: 180,
    intercept: -360,
    slopeDesc: '꼭짓점 1개 증가 → 내각의 합 180° 증가',
    interceptDesc: '기준값: (n-2)×180°에서 -360은 -(2×180°)',
    equation: '(n-2) × 180°',
    answer: '(5-2)×180° = 540°\n한 내각 = 540÷5 = 108°',
    whyGeometry: '다각형 내각의 합 공식은 정다각형 타일링·패턴·로고 설계에 필수입니다.',
    insight: '패션 디자이너·그래픽 디자이너·건축가는 다각형 내각 지식으로 정확한 패턴·로고·타일을 설계합니다.',
    realAnswer: 108,
    unit: '°',
    tableData: [
      { x: 3, y: 180 }, { x: 4, y: 360 }, { x: 5, y: 540 },
      { x: 6, y: 720 }, { x: 7, y: 900 }, { x: 8, y: 1080 },
    ],
    quiz: [
      { q: '정육각형 한 내각의 크기는?', a: '120°', hint: '(6-2)×180°=720°, 720÷6=120°' },
      { q: '내각의 합이 1080°인 다각형은 몇 각형?', a: '팔각형(8각형)', hint: '(n-2)×180°=1080° → n-2=6 → n=8' },
    ],
    jobs: ['패션 디자이너', '그래픽 디자이너', '건축가', '타일 시공사'],
  },
  {
    id: 'engineer',
    emoji: '🌉',
    title: '교량 구조 엔지니어',
    category: '토목·건설',
    color: 'from-orange-400 to-amber-500',
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    badge: 'bg-orange-100 text-orange-700',
    story: `교량 엔지니어 현우씨는 이등변삼각형 구조물을 설계합니다.
삼각형 꼭짓각이 40°일 때 두 밑각의 크기를 구해야 합니다.
"이등변삼각형의 두 밑각은 같고, 삼각형 내각의 합은 180°야!"
(180°-40°)÷2 = 70°임을 계산하여 구조물 각도를 결정했습니다.`,
    situation: '이등변삼각형: 꼭짓각 40°, 두 밑각은 서로 같음. 내각의 합 = 180°.',
    question: '이등변삼각형의 밑각 크기는 각각 몇 도인가요?',
    xLabel: '꼭짓각 (°)',
    yLabel: '밑각 (°)',
    slope: -0.5,
    intercept: 90,
    slopeDesc: '꼭짓각 2° 증가 → 밑각 1° 감소 (합=180°)',
    interceptDesc: '꼭짓각 0°일 때 밑각 90°',
    equation: '(180° - 꼭짓각) ÷ 2',
    answer: '(180° - 40°) ÷ 2 = 140° ÷ 2 = 70°\n각 밑각 = 70°',
    whyGeometry: '이등변삼각형 성질은 교량·지붕·트러스 구조에서 하중이 균등하게 분산되도록 설계하는 데 핵심입니다.',
    insight: '구조 엔지니어·건축사·항공기 설계사는 삼각형의 성질을 이용해 힘의 균형을 계산합니다.',
    realAnswer: 70,
    unit: '°',
    tableData: [
      { x: 20, y: 80 }, { x: 40, y: 70 }, { x: 60, y: 60 },
      { x: 80, y: 50 }, { x: 100, y: 40 }, { x: 120, y: 30 },
    ],
    quiz: [
      { q: '꼭짓각이 60°인 이등변삼각형의 밑각은?', a: '60°', hint: '(180-60)÷2=60°, 정삼각형!' },
      { q: '밑각이 75°인 이등변삼각형의 꼭짓각은?', a: '30°', hint: '75×2=150, 180-150=30°' },
    ],
    jobs: ['교량 엔지니어', '건축 구조사', '항공기 설계사', '트러스 기술자'],
  },
  {
    id: 'clockmaker',
    emoji: '⏰',
    title: '시계 기술자',
    category: '정밀·제조',
    color: 'from-violet-400 to-purple-500',
    bg: 'bg-violet-50',
    border: 'border-violet-200',
    badge: 'bg-violet-100 text-violet-700',
    story: `시계 기술자 지아씨는 지름 10cm 원형 시계판의 면적과 둘레를 계산합니다.
정밀한 케이스 제작을 위해 원의 넓이와 둘레가 필요합니다.
"지름 10cm이니까 반지름 r=5cm, 넓이=πr²=25π, 둘레=2πr=10π!"
약 78.5cm²의 넓이와 약 31.4cm의 둘레를 사용해 케이스를 제작했습니다.`,
    situation: '반지름 r=5cm인 원. 둘레=2πr=10π≈31.4cm, 넓이=πr²=25π≈78.5cm²',
    question: '반지름 5cm인 원의 넓이와 둘레를 구하세요.',
    xLabel: '반지름 r (cm)',
    yLabel: '둘레 (cm)',
    slope: 6.28,
    intercept: 0,
    slopeDesc: '반지름 1cm 증가 → 둘레 약 6.28cm 증가 (2π)',
    interceptDesc: '반지름 0이면 둘레 0 (원점 통과)',
    equation: '둘레=2πr, 넓이=πr²',
    answer: '둘레 = 2×π×5 = 10π ≈ 31.4cm\n넓이 = π×5² = 25π ≈ 78.5cm²',
    whyGeometry: '원의 넓이·둘레 공식은 시계·바퀴·파이프·렌즈 설계 등 원형 부품이 쓰이는 모든 산업에 필수입니다.',
    insight: '시계 기술자·자동차 엔지니어·광학 전문가는 원의 공식으로 정밀 부품의 크기와 재료량을 계산합니다.',
    realAnswer: 25,
    unit: 'π cm²',
    tableData: [
      { x: 1, y: 6.28 }, { x: 2, y: 12.57 }, { x: 3, y: 18.85 },
      { x: 4, y: 25.13 }, { x: 5, y: 31.42 }, { x: 6, y: 37.70 },
    ],
    quiz: [
      { q: '반지름 3cm인 원의 넓이는?', a: '9π cm²', hint: 'πr² = π×9 = 9π' },
      { q: '둘레가 12π cm인 원의 반지름은?', a: '6cm', hint: '2πr=12π → r=6' },
    ],
    jobs: ['시계 기술자', '자동차 엔지니어', '광학 전문가', '파이프 설계사'],
  },
  {
    id: 'baker',
    emoji: '🍕',
    title: '제과·제빵사',
    category: '식품·요리',
    color: 'from-rose-400 to-pink-500',
    bg: 'bg-rose-50',
    border: 'border-rose-200',
    badge: 'bg-rose-100 text-rose-700',
    story: `제빵사 민호씨는 피자 한 판(반지름 15cm)을 8등분으로 자릅니다.
한 조각은 중심각 45°인 부채꼴 모양입니다.
"부채꼴 호 길이 공식으로 빵 테두리 길이를 구할 수 있어!"
l = 2π×15×(45/360) = 30π×⅛ ≈ 11.8cm임을 계산했습니다.`,
    situation: '반지름 15cm, 중심각 45°(=360°÷8)인 부채꼴. 호의 길이 = 2πr×(θ/360°).',
    question: '피자 한 조각(r=15cm, θ=45°) 호의 길이는?',
    xLabel: '중심각 θ (°)',
    yLabel: '호의 길이 (cm)',
    slope: 0.262,
    intercept: 0,
    slopeDesc: '중심각 1° 증가 → 호의 길이 약 0.262cm 증가 (2πr/360)',
    interceptDesc: '중심각 0°이면 호 길이 0 (원점 통과)',
    equation: '2πr × (θ÷360°)',
    answer: '2π×15×(45÷360) = 30π×(1/8) = 3.75π ≈ 11.78cm',
    whyGeometry: '부채꼴 공식은 피자·케이크 분배, 시계 바늘 이동거리, 기어·팬 설계에 모두 활용됩니다.',
    insight: '제빵사·기계설계사·시계 엔지니어는 부채꼴의 호 길이와 넓이 공식으로 정확한 치수를 계산합니다.',
    realAnswer: 11.78,
    unit: 'cm',
    tableData: [
      { x: 30, y: 7.85 }, { x: 45, y: 11.78 }, { x: 60, y: 15.71 },
      { x: 90, y: 23.56 }, { x: 120, y: 31.42 }, { x: 180, y: 47.12 },
    ],
    quiz: [
      { q: 'r=12cm, θ=60°인 부채꼴의 호 길이는?', a: '4π cm', hint: '2π×12×(60/360)=24π×(1/6)=4π' },
      { q: 'r=9cm, θ=120°인 부채꼴의 넓이는?', a: '27π cm²', hint: 'πr²×(120/360)=81π×(1/3)=27π' },
    ],
    jobs: ['제과·제빵사', '기계설계사', '시계 엔지니어', '건축 모델러'],
  },
  {
    id: 'landscape',
    emoji: '🌿',
    title: '조경 설계사',
    category: '조경·환경',
    color: 'from-green-400 to-emerald-500',
    bg: 'bg-green-50',
    border: 'border-green-200',
    badge: 'bg-green-100 text-green-700',
    story: `조경 설계사 유나씨는 공원에 정팔각형 분수대를 설계합니다.
정팔각형의 한 내각 크기를 알아야 경계석을 올바른 각도로 배치할 수 있습니다.
"(8-2)×180°÷8 = 135°야! 한 외각은 360°÷8 = 45°이고!"
외각 45°씩 꺾으면서 정팔각형 경계를 완성했습니다.`,
    situation: '정팔각형(n=8): 내각의 합=(8-2)×180°=1080°, 한 내각=1080°÷8=135°, 한 외각=45°.',
    question: '정팔각형 한 내각과 한 외각의 크기는 각각 몇 도인가요?',
    xLabel: '꼭짓점 수 n',
    yLabel: '한 내각 (°)',
    slope: 0,
    intercept: 0,
    slopeDesc: '꼭짓점 수에 따라 한 내각 = (n-2)×180°÷n',
    interceptDesc: 'n이 클수록 정다각형이 원에 가까워짐',
    equation: '한 내각 = (n-2)×180°÷n',
    answer: '내각의 합 = (8-2)×180° = 1080°\n한 내각 = 1080°÷8 = 135°\n한 외각 = 360°÷8 = 45°',
    whyGeometry: '정다각형 내각·외각 계산은 분수대·공원 설계, 건물 평면, 타일 패턴 시공에 꼭 필요합니다.',
    insight: '조경 설계사·도시계획가·인테리어 디자이너는 다각형 각도 지식으로 아름다운 공간을 설계합니다.',
    realAnswer: 135,
    unit: '°',
    tableData: [
      { x: 3, y: 60 }, { x: 4, y: 90 }, { x: 5, y: 108 },
      { x: 6, y: 120 }, { x: 8, y: 135 }, { x: 12, y: 150 },
    ],
    quiz: [
      { q: '정십이각형 한 내각의 크기는?', a: '150°', hint: '(12-2)×180°÷12=1800°÷12=150°' },
      { q: '정다각형의 한 외각이 30°이면 몇 각형인가요?', a: '정십이각형', hint: '360°÷30°=12 → 12각형' },
    ],
    jobs: ['조경 설계사', '도시계획가', '인테리어 디자이너', '타일 시공사'],
  },
]

// ── 직업 탐색 데이터 ──────────────────────────────────────────────────────────
const CAREERS = [
  {
    icon: '🎨',
    title: '패션·그래픽 디자이너',
    example: '정다각형 내각으로 패턴·로고 정확히 배치',
    formula: '내각의 합 = (n-2)×180°,  한 내각 = ÷n',
    salary: '연 3,500만~8,000만원',
    color: 'from-emerald-500 to-teal-500',
  },
  {
    icon: '🌉',
    title: '교량·구조 엔지니어',
    example: '삼각형 성질로 트러스 구조 각도 계산',
    formula: '이등변삼각형 밑각 = (180°-꼭짓각)÷2',
    salary: '연 4,500만~1억원',
    color: 'from-orange-500 to-amber-500',
  },
  {
    icon: '⏰',
    title: '정밀 기계·시계 기술자',
    example: '원형 부품의 둘레·넓이로 재료량 계산',
    formula: 'C = 2πr,  A = πr²',
    salary: '연 4,000만~9,000만원',
    color: 'from-violet-500 to-purple-500',
  },
  {
    icon: '🍕',
    title: '식품·제과 기술자',
    example: '부채꼴 호 길이로 등분 크기 정확 계산',
    formula: 'l = 2πr×θ/360°,  S = πr²×θ/360°',
    salary: '연 3,000만~6,000만원',
    color: 'from-rose-500 to-pink-500',
  },
  {
    icon: '🌿',
    title: '조경·도시 설계사',
    example: '정다각형 내각·외각으로 공원·광장 설계',
    formula: '외각의 합 = 360°,  한 외각 = 360°÷n',
    salary: '연 4,000만~8,000만원',
    color: 'from-green-500 to-emerald-500',
  },
  {
    icon: '🔭',
    title: '천문·위성 공학자',
    example: '원·부채꼴로 위성 궤도 면적·거리 계산',
    formula: '부채꼴 넓이 S = ½lr (l=호, r=반지름)',
    salary: '연 5,000만~1.2억원',
    color: 'from-indigo-500 to-violet-500',
  },
]

// ── 도전 퀴즈 ─────────────────────────────────────────────────────────────────
const CHALLENGES = [
  {
    id: 'c1',
    emoji: '🔷',
    level: '⭐ 쉬움',
    levelColor: 'bg-emerald-100 text-emerald-700',
    question: '칠각형(7각형)의 내각의 합을 구하고, 정칠각형 한 내각의 크기를 구하시오.',
    hint: '내각의 합 = (7-2)×180°, 정칠각형 한 내각 = 합÷7',
    answer: '내각의 합 = 900°, 한 내각 ≈ 128.6°',
    explanation: '(7-2)×180° = 5×180° = 900°. 정칠각형 한 내각 = 900°÷7 ≈ 128.57°',
  },
  {
    id: 'c2',
    emoji: '📐',
    level: '⭐⭐ 보통',
    levelColor: 'bg-amber-100 text-amber-700',
    question: '이등변삼각형 ABC에서 AB=AC, ∠A=50°. 이 삼각형은 예각·직각·둔각 중 어느 삼각형인가요? 세 각의 크기도 구하세요.',
    hint: '밑각=(180-50)÷2=65°. 세 각이 50°, 65°, 65° → 모두 90°보다 작으므로?',
    answer: '예각삼각형. 세 각: ∠A=50°, ∠B=∠C=65°',
    explanation: '밑각=(180°-50°)÷2=65°. 세 각 50°, 65°, 65° 모두 90° 미만 → 예각삼각형!',
  },
  {
    id: 'c3',
    emoji: '⏰',
    level: '⭐⭐ 보통',
    levelColor: 'bg-amber-100 text-amber-700',
    question: '반지름 10cm인 원에서 중심각이 144°인 부채꼴의 호 길이와 넓이를 구하세요.',
    hint: '144÷360=2/5. 호 길이=2π×10×(2/5), 넓이=π×100×(2/5)',
    answer: '호 길이=8π cm, 넓이=40π cm²',
    explanation: 'l=2π×10×(144/360)=20π×(2/5)=8π cm. S=π×100×(2/5)=40π cm²',
  },
  {
    id: 'c4',
    emoji: '🌿',
    level: '⭐⭐⭐ 어려움',
    levelColor: 'bg-red-100 text-red-700',
    question: '넓이가 24π cm²이고 호의 길이가 8π cm인 부채꼴의 반지름과 중심각을 구하시오.',
    hint: 'S=½lr → 24π=½×8π×r. r을 구한 후 l=2πr×θ/360°으로 θ를 구하세요.',
    answer: '반지름=6cm, 중심각=240°',
    explanation: '24π=½×8π×r → 24π=4πr → r=6cm. 8π=2π×6×(θ/360°) → 8π=12π×θ/360° → θ=240°',
  },
]

const CATEGORY_COLORS: Record<string, string> = {
  '패션·섬유': 'bg-emerald-100 text-emerald-700',
  '토목·건설': 'bg-orange-100 text-orange-700',
  '정밀·제조': 'bg-violet-100 text-violet-700',
  '식품·요리': 'bg-rose-100 text-rose-700',
  '조경·환경': 'bg-green-100 text-green-700',
}

// ── 계산기 컴포넌트 ───────────────────────────────────────────────────────────
type CalcMode = 'forward' | 'inverse'

interface CalcResult {
  mode: 'forward' | 'inverse'
  inputVal: number
  outputVal: number
  steps: string[]
}

function ScenarioCalculator({ scenario }: { scenario: typeof SCENARIOS[0] }) {
  const [mode, setMode] = useState<CalcMode>('forward')
  const [inputVal, setInputVal] = useState('')
  const [result, setResult] = useState<CalcResult | null>(null)
  const [userAnswer, setUserAnswer] = useState('')
  const [checked, setChecked] = useState<boolean | null>(null)

  const reset = () => { setInputVal(''); setResult(null); setUserAnswer(''); setChecked(null) }
  const handleModeChange = (m: CalcMode) => { setMode(m); reset() }

  const calculate = () => {
    const v = parseFloat(inputVal)
    if (isNaN(v)) return

    if (mode === 'forward') {
      const raw = scenario.slope * v + scenario.intercept
      const y = Math.round(raw * 100) / 100
      setResult({
        mode: 'forward',
        inputVal: v,
        outputVal: y,
        steps: [
          `공식: ${scenario.equation}`,
          `x = ${v} 대입`,
          `y = ${scenario.slope} × ${v} + (${scenario.intercept})`,
          `y = ${scenario.slope * v} + (${scenario.intercept})`,
          `y = ${y.toLocaleString()} ${scenario.unit}`,
        ],
      })
    } else {
      if (scenario.slope === 0) return
      const raw = (v - scenario.intercept) / scenario.slope
      const x = Math.round(raw * 100) / 100
      setResult({
        mode: 'inverse',
        inputVal: v,
        outputVal: x,
        steps: [
          `방정식: ${scenario.equation}`,
          `목표값 = ${v.toLocaleString()}으로 설정`,
          `${scenario.slope}x = ${v} − (${scenario.intercept}) = ${v - scenario.intercept}`,
          `x = ${v - scenario.intercept} ÷ ${scenario.slope}`,
          `x = ${x.toLocaleString()} ${scenario.xLabel.split(' ')[1]?.replace(/[()]/g, '') || ''}`,
        ],
      })
    }
    setUserAnswer('')
    setChecked(null)
  }

  const checkAnswer = () => {
    if (!result) return
    const ua = parseFloat(userAnswer.replace(/,/g, ''))
    const tolerance = Math.abs(result.outputVal) * 0.02 + 1
    setChecked(Math.abs(ua - result.outputVal) <= tolerance)
  }

  const xLabelShort = scenario.xLabel.split(' ')[0]
  const yLabelShort = scenario.yLabel.split(' ')[0]

  return (
    <div className="card border-2 border-emerald-100">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm font-bold text-slate-700 flex items-center gap-2">
          <Calculator size={15} className="text-emerald-600" />
          직접 계산해보기
        </p>
        <div className="flex rounded-lg overflow-hidden border border-slate-200 text-xs">
          <button
            onClick={() => handleModeChange('forward')}
            className={`px-3 py-1.5 font-medium transition-colors flex items-center gap-1 ${
              mode === 'forward' ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <ArrowRight size={11} />
            {xLabelShort} → {yLabelShort}
          </button>
          <button
            onClick={() => handleModeChange('inverse')}
            className={`px-3 py-1.5 font-medium transition-colors flex items-center gap-1 ${
              mode === 'inverse' ? 'bg-teal-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <ArrowLeftRight size={11} />
            역산 (풀기)
          </button>
        </div>
      </div>

      <div className="mb-4 bg-slate-50 rounded-xl px-4 py-3 flex items-center gap-3">
        <span className="text-xs text-slate-400 font-medium">공식</span>
        <span className="font-mono font-bold text-emerald-700 text-lg">{scenario.equation}</span>
      </div>

      <div className="flex gap-3 mb-4">
        <div className="flex-1">
          <label className="text-xs font-semibold text-slate-500">
            {mode === 'forward'
              ? `🔢 ${scenario.xLabel} 를 입력하세요`
              : `🎯 목표 ${scenario.yLabel} 를 입력하세요`}
          </label>
          <input
            type="number"
            value={inputVal}
            onChange={e => { setInputVal(e.target.value); setResult(null); setChecked(null) }}
            onKeyDown={e => e.key === 'Enter' && calculate()}
            placeholder="숫자를 입력하고 계산!"
            className="w-full mt-1 text-base border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-emerald-300 focus:border-emerald-300 transition-all"
          />
        </div>
        <div className="flex items-end">
          <button
            onClick={calculate}
            disabled={!inputVal}
            className={`px-5 py-2.5 rounded-xl font-bold text-sm transition-all ${
              mode === 'forward'
                ? 'bg-emerald-600 hover:bg-emerald-700 text-white disabled:bg-slate-200 disabled:text-slate-400'
                : 'bg-teal-600 hover:bg-teal-700 text-white disabled:bg-slate-200 disabled:text-slate-400'
            }`}
          >
            계산!
          </button>
        </div>
      </div>

      {result && (
        <div className="space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-xs font-bold text-amber-700 mb-3 flex items-center gap-1">
              📝 풀이 과정 (단계별)
            </p>
            <div className="space-y-1.5">
              {result.steps.map((step, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className={`text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    i === result.steps.length - 1 ? 'bg-emerald-500 text-white' : 'bg-amber-200 text-amber-700'
                  }`}>{i + 1}</span>
                  <span className={`text-sm font-mono ${
                    i === result.steps.length - 1 ? 'font-black text-emerald-700 text-base' : 'text-amber-800'
                  }`}>{step}</span>
                </div>
              ))}
            </div>
          </div>

          <div className={`rounded-xl px-5 py-4 border-2 ${
            mode === 'forward' ? 'bg-emerald-50 border-emerald-300' : 'bg-teal-50 border-teal-300'
          }`}>
            <p className="text-xs font-semibold text-slate-500 mb-1">
              {mode === 'forward'
                ? `${scenario.xLabel} = ${result.inputVal} 일 때`
                : `목표 ${scenario.yLabel} = ${result.inputVal.toLocaleString()} ${scenario.unit} 일 때`}
            </p>
            <div className="flex items-baseline gap-2">
              <span className={`text-3xl font-black ${mode === 'forward' ? 'text-emerald-700' : 'text-teal-700'}`}>
                {result.outputVal.toLocaleString()}
              </span>
              <span className="text-base font-semibold text-slate-500">
                {mode === 'forward' ? scenario.unit : scenario.xLabel.split(' ')[1]?.replace(/[()]/g, '') || ''}
              </span>
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
            <p className="text-xs font-bold text-slate-600 mb-2">🧑‍🎓 답 맞춰보기</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={userAnswer}
                onChange={e => { setUserAnswer(e.target.value); setChecked(null) }}
                onKeyDown={e => e.key === 'Enter' && checkAnswer()}
                placeholder={`내 답: __ ${mode === 'forward' ? scenario.unit : ''}`}
                className={`flex-1 text-sm border-2 rounded-lg px-3 py-2 focus:outline-none transition-all ${
                  checked === null ? 'border-slate-200' : checked ? 'border-emerald-400 bg-emerald-50' : 'border-red-400 bg-red-50'
                }`}
              />
              <button
                onClick={checkAnswer}
                disabled={!userAnswer}
                className="px-4 py-2 rounded-lg bg-slate-700 text-white text-sm font-medium disabled:bg-slate-200 disabled:text-slate-400 hover:bg-slate-800 transition-colors"
              >
                확인
              </button>
            </div>
            {checked !== null && (
              <div className={`mt-2 flex items-center gap-2 text-sm font-semibold ${checked ? 'text-emerald-600' : 'text-red-600'}`}>
                {checked
                  ? <><CheckCircle size={16} /> 정답이에요! 🎉</>
                  : <><XCircle size={16} /> 풀이 과정을 다시 확인해보세요!</>}
              </div>
            )}
          </div>
          <button onClick={reset} className="text-xs text-slate-400 hover:text-slate-600 transition-colors">
            ↺ 다시 입력하기
          </button>
        </div>
      )}
    </div>
  )
}

// ── 퀴즈 컴포넌트 ─────────────────────────────────────────────────────────────
function QuizSection({ quiz }: { quiz: typeof SCENARIOS[0]['quiz'] }) {
  const [revealed, setRevealed] = useState<Record<number, boolean>>({})
  const [showHint, setShowHint] = useState<Record<number, boolean>>({})

  return (
    <div className="space-y-3">
      {quiz.map((q, i) => (
        <div key={i} className="border border-slate-200 rounded-xl p-4 bg-white">
          <p className="text-sm font-semibold text-slate-800 mb-2">❓ {q.q}</p>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setShowHint(h => ({ ...h, [i]: !h[i] }))}
              className="text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors flex items-center gap-1"
            >
              <Lightbulb size={11} /> 힌트
            </button>
            <button
              onClick={() => setRevealed(r => ({ ...r, [i]: !r[i] }))}
              className="text-xs px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors"
            >
              {revealed[i] ? '정답 숨기기' : '정답 보기'}
            </button>
          </div>
          {showHint[i] && (
            <div className="mt-2 bg-amber-50 rounded-lg px-3 py-2 text-xs text-amber-700">
              💡 힌트: <span className="font-mono">{q.hint}</span>
            </div>
          )}
          {revealed[i] && (
            <div className="mt-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
              <span className="text-sm font-bold text-emerald-700">✅ 정답: {q.a}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// ── 챌린지 카드 컴포넌트 ──────────────────────────────────────────────────────
function ChallengeCard({ ch }: { ch: typeof CHALLENGES[0] }) {
  const [showHint, setShowHint] = useState(false)
  const [revealed, setRevealed] = useState(false)

  return (
    <div className="card border border-slate-200">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{ch.emoji}</span>
          <span className={`text-xs px-2 py-1 rounded-full font-medium ${ch.levelColor}`}>{ch.level}</span>
        </div>
      </div>
      <p className="text-sm text-slate-800 font-medium leading-relaxed mb-3">{ch.question}</p>
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setShowHint(h => !h)}
          className="text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors flex items-center gap-1"
        >
          <Lightbulb size={11} /> 힌트
        </button>
        <button
          onClick={() => setRevealed(r => !r)}
          className="text-xs px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors flex items-center gap-1"
        >
          <Trophy size={11} /> {revealed ? '정답 숨기기' : '정답 확인'}
        </button>
      </div>
      {showHint && (
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
          💡 {ch.hint}
        </div>
      )}
      {revealed && (
        <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-xl p-3 space-y-2">
          <p className="text-sm font-bold text-emerald-700">✅ 정답: {ch.answer}</p>
          <p className="text-xs text-emerald-600 leading-relaxed">{ch.explanation}</p>
        </div>
      )}
    </div>
  )
}

// ── 메인 페이지 ───────────────────────────────────────────────────────────────
type Tab = 'scenarios' | 'careers' | 'challenges'

export default function G1_2RealWorldPage() {
  const [selected, setSelected] = useState(SCENARIOS[0])
  const [showTable, setShowTable] = useState(false)
  const [filterCat, setFilterCat] = useState<string>('전체')
  const [activeTab, setActiveTab] = useState<Tab>('scenarios')

  const categories = ['전체', ...Array.from(new Set(SCENARIOS.map(s => s.category)))]
  const filtered = filterCat === '전체' ? SCENARIOS : SCENARIOS.filter(s => s.category === filterCat)

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'scenarios', label: '시나리오 탐색', icon: <MapPin size={14} /> },
    { id: 'careers', label: '직업과 평면도형', icon: <Briefcase size={14} /> },
    { id: 'challenges', label: '도전 문제', icon: <Trophy size={14} /> },
  ]

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
            <Globe size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">꿈꾸는 직업과 평면도형</h1>
            <p className="text-sm text-slate-500">패션 디자이너, 교량 엔지니어, 시계 기술자, 제빵사, 조경사 — 모두 평면도형을 활용합니다!</p>
          </div>
        </div>

        <div className="mt-4 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-5">
          <p className="font-bold text-lg mb-1">🌟 꿈의 직업에서 평면도형을 만나다!</p>
          <p className="text-sm opacity-90 mb-3">다각형·삼각형·원·부채꼴의 성질이 세상 모든 설계와 제조의 기초입니다.</p>
          <div className="grid grid-cols-4 md:grid-cols-5 gap-2">
            {[
              { icon: '🎨', job: '패션 디자이너' },
              { icon: '🌉', job: '교량 엔지니어' },
              { icon: '⏰', job: '시계 기술자' },
              { icon: '🍕', job: '제빵사' },
              { icon: '🌿', job: '조경 설계사' },
            ].map(({ icon, job }) => (
              <div key={job} className="bg-white/20 rounded-xl px-2 py-2 text-center">
                <p className="text-2xl">{icon}</p>
                <p className="text-xs font-bold mt-1">{job}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-slate-100 p-1 rounded-xl w-fit">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-white text-emerald-700 shadow-sm'
                : 'text-slate-600 hover:text-slate-800'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── TAB 1: 시나리오 ── */}
      {activeTab === 'scenarios' && (
        <>
          <div className="flex gap-2 mb-4 flex-wrap">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setFilterCat(cat)}
                className={`text-xs px-4 py-2 rounded-full font-medium transition-colors ${
                  filterCat === cat
                    ? 'bg-emerald-600 text-white'
                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* 좌측: 시나리오 목록 */}
            <div className="space-y-3">
              {filtered.map(scenario => (
                <button
                  key={scenario.id}
                  onClick={() => { setSelected(scenario); setShowTable(false) }}
                  className={`w-full text-left rounded-2xl border-2 p-4 transition-all ${
                    selected.id === scenario.id
                      ? `${scenario.border} ${scenario.bg} shadow-md`
                      : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{scenario.emoji}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-slate-800">{scenario.title}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[scenario.category]}`}>
                          {scenario.category}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{scenario.situation}</p>
                      <div className="mt-1.5 font-mono text-xs text-emerald-600 font-semibold">{scenario.equation}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* 우측: 시나리오 상세 */}
            <div className="lg:col-span-2 space-y-4">
              <div className={`rounded-2xl border-2 ${selected.border} ${selected.bg} p-5`}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-4xl">{selected.emoji}</span>
                  <div>
                    <h2 className="text-xl font-bold text-slate-800">{selected.title}</h2>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[selected.category]}`}>
                      {selected.category}
                    </span>
                  </div>
                </div>

                <div className="mb-4 bg-white/70 rounded-xl p-4 border border-slate-100">
                  <p className="text-xs font-bold text-slate-500 mb-2">📖 이야기로 만나는 평면도형</p>
                  <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{selected.story}</p>
                </div>

                <p className="text-sm font-semibold text-slate-600 mb-1">📌 수학적 상황</p>
                <p className="text-sm text-slate-700 leading-relaxed mb-3">{selected.situation}</p>

                <div className="bg-white rounded-xl p-4 border border-slate-100">
                  <p className="text-xs font-semibold text-slate-500 mb-3">📐 도형 공식으로 나타내기</p>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-2xl font-black text-emerald-700 font-mono">{selected.equation}</span>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-2.5">
                      <p className="text-xs text-orange-500 font-semibold">변화율 (= {selected.slope})</p>
                      <p className="text-xs text-orange-700 mt-0.5">{selected.slopeDesc}</p>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5">
                      <p className="text-xs text-blue-500 font-semibold">기준값 (= {selected.intercept})</p>
                      <p className="text-xs text-blue-700 mt-0.5">{selected.interceptDesc}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <p className="text-sm font-semibold text-slate-700 mb-2">💬 문제</p>
                <p className="text-base text-slate-800 font-medium">{selected.question}</p>
                <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
                  <p className="text-sm font-bold text-emerald-700 whitespace-pre-line">✅ 풀이: {selected.answer}</p>
                </div>
                <div className="mt-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                  <p className="text-xs text-emerald-700">
                    <span className="font-semibold">왜 평면도형인가?</span> {selected.whyGeometry}
                  </p>
                </div>
              </div>

              <div className="card">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <TrendingUp size={15} className="text-emerald-500" /> 표로 보기
                  </p>
                  <button
                    onClick={() => setShowTable(!showTable)}
                    className="text-xs flex items-center gap-1 text-slate-500 hover:text-slate-700 bg-slate-100 px-3 py-1 rounded-full"
                  >
                    {showTable ? '표 접기' : '표 펼치기'}
                    {showTable ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  </button>
                </div>
                {showTable && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr>
                          <th className="border border-slate-200 px-3 py-2 bg-orange-50 text-orange-700 text-left">{selected.xLabel}</th>
                          <th className="border border-slate-200 px-3 py-2 bg-emerald-50 text-emerald-700 text-left">{selected.yLabel}</th>
                          <th className="border border-slate-200 px-3 py-2 bg-slate-50 text-slate-600 text-left">변화율</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selected.tableData.map((row, i) => (
                          <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                            <td className="border border-slate-200 px-3 py-2 font-mono font-bold text-orange-600">{row.x}</td>
                            <td className="border border-slate-200 px-3 py-2 font-mono font-bold text-emerald-700">{row.y.toLocaleString()}</td>
                            <td className="border border-slate-200 px-3 py-2 text-xs text-slate-500">
                              {i === 0 ? '—' : (
                                <span className="font-mono text-emerald-600">
                                  {((row.y - selected.tableData[i - 1].y) / (row.x - selected.tableData[i - 1].x)).toFixed(2)}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {!showTable && (
                  <div className="bg-slate-50 rounded-xl p-4 text-center text-slate-500 text-sm">
                    <p className="text-2xl mb-1">📋</p>
                    <p>표 펼치기를 눌러 값의 변화를 확인해보세요!</p>
                  </div>
                )}
              </div>

              <ScenarioCalculator scenario={selected} />

              <div className="card">
                <p className="text-sm font-semibold text-slate-700 mb-3">🧩 연습 문제</p>
                <QuizSection quiz={selected.quiz} />
              </div>

              <div className="card bg-slate-50 border border-slate-200">
                <p className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Briefcase size={14} className="text-emerald-500" /> 이 상황에서 평면도형을 쓰는 직업
                </p>
                <div className="flex flex-wrap gap-2">
                  {selected.jobs.map(job => (
                    <span key={job} className="text-xs bg-emerald-100 text-emerald-700 px-3 py-1.5 rounded-full font-medium flex items-center gap-1">
                      <Star size={10} /> {job}
                    </span>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl bg-gradient-to-r from-slate-800 to-slate-700 text-white p-5">
                <p className="text-sm font-bold mb-1 flex items-center gap-2">
                  <TrendingUp size={15} /> 실제로 어디서 쓰이나요?
                </p>
                <p className="text-sm text-slate-300 leading-relaxed">{selected.insight}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {['다각형', '삼각형', '원', '부채꼴'].map(tag => (
                    <span key={tag} className="text-xs bg-white/10 px-2 py-1 rounded-full">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 card bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200">
            <h3 className="font-bold text-slate-800 mb-4 text-base">📌 평면도형 실생활 핵심 공식</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { icon: '🔷', title: '다각형 내각의 합', desc: 'n각형 내각의 합 = (n-2)×180°. 정n각형 한 내각 = ÷n. 외각의 합 = 항상 360°!', color: 'text-emerald-600' },
                { icon: '📐', title: '삼각형 성질', desc: '내각의 합 = 180°. 이등변삼각형: 밑각 같음. 정삼각형: 각 60°. 구조 설계의 기본!', color: 'text-teal-600' },
                { icon: '⭕', title: '원·부채꼴 공식', desc: '원 둘레=2πr, 넓이=πr². 부채꼴 호=2πrθ/360°, 넓이=½lr. 모든 원형 설계 핵심!', color: 'text-cyan-600' },
              ].map(({ icon, title, desc, color }) => (
                <div key={title} className="bg-white rounded-xl p-4 border border-slate-100">
                  <p className="text-2xl mb-2">{icon}</p>
                  <p className={`font-bold text-sm ${color}`}>{title}</p>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ── TAB 2: 직업 탐색 ── */}
      {activeTab === 'careers' && (
        <div>
          <div className="mb-6 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-6">
            <h2 className="text-xl font-bold mb-2">💼 평면도형을 사용하는 직업들</h2>
            <p className="text-sm text-emerald-100">
              다각형·삼각형·원·부채꼴의 성질은 설계·제조·조경·식품 등 수많은 직업의 핵심 도구입니다!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
            {CAREERS.map(career => (
              <div key={career.title} className="card border border-slate-200 hover:shadow-md transition-shadow">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${career.color} flex items-center justify-center text-2xl mb-3`}>
                  {career.icon}
                </div>
                <h3 className="font-bold text-slate-800 mb-1">{career.title}</h3>
                <p className="text-xs text-slate-500 mb-3 leading-relaxed">{career.example}</p>
                <div className="bg-slate-50 rounded-lg p-3 mb-3">
                  <p className="text-xs text-slate-400 mb-1">실제 사용 공식</p>
                  <code className="text-xs font-mono text-emerald-700 font-bold">{career.formula}</code>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full font-medium">
                    💰 {career.salary}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="card border border-slate-200 bg-gradient-to-br from-slate-800 to-slate-900 text-white mb-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Zap size={20} className="text-yellow-400" /> AI 시대에도 평면도형이 필요한 이유
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { title: '컴퓨터 그래픽·UI 디자인', desc: '화면의 모든 UI 요소는 다각형·원 기반. 내각·둘레 계산 없이 완벽한 레이아웃 불가!', color: 'border-yellow-400' },
                { title: '로봇·드론 경로 계획', desc: '장애물 우회·착지 각도 계산에 삼각형 성질과 내각 공식을 활용합니다.', color: 'border-emerald-400' },
                { title: '건축·도시 설계 CAD', desc: '정다각형 광장·원형 분수대 설계는 내각·부채꼴 공식으로 자동 계산됩니다.', color: 'border-blue-400' },
                { title: '위성·GPS 측위', desc: '원형 위성 궤도의 호 길이와 넓이를 부채꼴 공식으로 계산합니다.', color: 'border-purple-400' },
              ].map(item => (
                <div key={item.title} className={`border-l-4 ${item.color} pl-4 py-2`}>
                  <p className="font-semibold text-sm mb-1">{item.title}</p>
                  <p className="text-xs text-slate-300 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="card border border-emerald-200 bg-emerald-50">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
              <ChevronRight size={18} className="text-emerald-600" /> 평면도형에서 시작하는 학습 경로
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              {[
                { step: '중1', topic: '기본 도형', active: false },
                { step: '중1', topic: '평면도형', active: true },
                { step: '중1', topic: '입체도형', active: false },
                { step: '중2', topic: '삼각형·사각형', active: false },
                { step: '중3', topic: '피타고라스', active: false },
                { step: '고1', topic: '삼각비', active: false },
                { step: '직업', topic: 'CAD·설계·제조', active: false },
              ].map((item, i) => (
                <React.Fragment key={`${item.step}-${item.topic}`}>
                  <div className={`rounded-xl px-3 py-2 text-center ${item.active ? 'bg-emerald-600 text-white' : 'bg-white border border-slate-200 text-slate-600'}`}>
                    <p className="text-xs font-bold">{item.step}</p>
                    <p className="text-xs mt-0.5">{item.topic}</p>
                  </div>
                  {i < 6 && <ChevronRight size={14} className="text-slate-400 flex-shrink-0" />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── TAB 3: 도전 문제 ── */}
      {activeTab === 'challenges' && (
        <div>
          <div className="mb-6 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white p-6">
            <h2 className="text-xl font-bold mb-2">🏆 실생활 도전 문제</h2>
            <p className="text-sm text-emerald-100">
              실제 상황에서 평면도형의 성질을 직접 적용해보세요!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
            {CHALLENGES.map(ch => (
              <ChallengeCard key={ch.id} ch={ch} />
            ))}
          </div>

          <div className="card border border-slate-200">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Star size={16} className="text-emerald-500" /> 평면도형 핵심 공식 정리
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { step: '① 다각형 각의 성질', desc: '내각의 합=(n-2)×180°\n정n각형 한 내각=÷n\n외각의 합=360° (항상!)', example: '정육각형 한 내각=120°', color: 'bg-emerald-50 border-emerald-200 text-emerald-800' },
                { step: '② 삼각형 성질', desc: '내각의 합=180°\n이등변삼각형: 밑각 같음\n정삼각형: 모든 각 60°', example: '(180°-꼭짓각)÷2=밑각', color: 'bg-teal-50 border-teal-200 text-teal-800' },
                { step: '③ 원·부채꼴 공식', desc: '원: C=2πr, A=πr²\n부채꼴 호: l=2πr×θ/360°\n부채꼴 넓이: S=½lr', example: '√(3²+4²)=5 (보너스!)', color: 'bg-cyan-50 border-cyan-200 text-cyan-800' },
              ].map(item => (
                <div key={item.step} className={`border rounded-xl p-4 ${item.color}`}>
                  <p className="font-bold text-sm mb-2">{item.step}</p>
                  <p className="text-xs leading-relaxed mb-2 whitespace-pre-line">{item.desc}</p>
                  <div className="bg-white/60 rounded-lg px-2 py-1">
                    <p className="text-xs font-mono font-bold">{item.example}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
              <p className="text-sm font-bold text-emerald-700">다각형 → 삼각형 → 원 → 부채꼴 → 설계!</p>
              <p className="text-xs text-emerald-500 mt-1">평면도형의 성질을 알면 세상 모든 평면을 수식으로 표현할 수 있습니다!</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
